import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, FolderPlus, Folder } from "lucide-react";
import { toast } from "sonner";
import { useCategories } from "../hooks/useCategories";
import { COLOR_SCHEMES, COLOR_OPTIONS } from "../lib/categories";
import { createCategory, deleteCategory } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const CategoryManager = ({ open, onClose }) => {
  const qc = useQueryClient();
  const { custom } = useCategories();
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("teal");
  const [busy, setBusy] = useState("");

  const refresh = () => { qc.invalidateQueries({ queryKey: ["categories"] }); qc.invalidateQueries({ queryKey: ["documents"] }); };

  const add = async () => {
    const name = label.trim();
    if (!name) return;
    setBusy("add");
    try {
      await createCategory(name, color);
      toast.success(`Category "${name}" added`);
      setLabel("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that category");
    } finally { setBusy(""); }
  };

  const remove = async (c) => {
    setBusy(`d-${c.key}`);
    try {
      const res = await deleteCategory(c.key);
      toast.success(`Deleted "${c.label}"`, { description: res.reassigned ? `${res.reassigned} document(s) moved to Misc` : undefined });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete that category");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="category-manager" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><FolderPlus className="h-5 w-5 text-primary" /> Your categories</DialogTitle>
          <DialogDescription>Create your own filing categories. The AI will file into them automatically, and you can re-file any document by hand.</DialogDescription>
        </DialogHeader>

        {/* Add form */}
        <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
          <Input
            data-testid="new-category-name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Pets, Vehicles, School…"
            maxLength={40}
            className="bg-card"
          />
          <div className="flex items-center gap-2 flex-wrap" data-testid="category-color-picker">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`color-${c}`}
                aria-label={c}
                onClick={() => setColor(c)}
                className={`h-7 w-7 rounded-full ${COLOR_SCHEMES[c].dot} transition-transform ${color === c ? "ring-2 ring-offset-2 ring-foreground/60 scale-110" : "hover:scale-105"}`}
              />
            ))}
          </div>
          <Button data-testid="add-category-btn" onClick={add} disabled={busy === "add" || !label.trim()} className="w-full gap-2">
            {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add category
          </Button>
        </div>

        {/* Existing custom list */}
        <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="custom-category-list">
          {custom.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">No custom categories yet — add your first above.</p>
          ) : custom.map((c) => {
            const scheme = COLOR_SCHEMES[c.color] || COLOR_SCHEMES.stone;
            return (
              <div key={c.key} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2" data-testid={`custom-category-${c.key}`}>
                <span className={`h-8 w-8 rounded-lg grid place-items-center ${scheme.tint}`}><Folder className="h-4 w-4" style={{ color: scheme.iconColor }} /></span>
                <span className="flex-1 text-sm font-medium">{c.label}</span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-category-${c.key}`} disabled={!!busy} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-50">
                      {busy === `d-${c.key}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-category-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{c.label}"?</AlertDialogTitle>
                      <AlertDialogDescription>Any documents filed here will be moved to Misc. This can't be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-category-cancel">Keep it</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-category-confirm-btn" onClick={() => remove(c)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
